import {ariaLabelDate} from '@github-ui/aria-label-date'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {isLoggedIn} from '@github-ui/client-env'
import {copyText} from '@github-ui/copy-to-clipboard'
import type {CreateIssueDialogEntryProps} from '@github-ui/issue-create/CreateIssueDialogEntry'
import type {CommentAuthorAssociation as GraphQlCommentAuthorAssociation} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  BlockedIcon,
  EyeClosedIcon,
  EyeIcon,
  FoldIcon,
  IssueOpenedIcon,
  KebabHorizontalIcon,
  LinkIcon,
  MarkdownIcon,
  PencilIcon,
  PinIcon,
  PinSlashIcon,
  QuoteIcon,
  ReportIcon,
  TrashIcon,
  UnfoldIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, ConfirmationDialog, IconButton} from '@primer/react'
import React, {Suspense, useCallback, useMemo, useState} from 'react'

import {TEST_IDS} from '../constants/test-ids'
import {unblockUserPersonally} from '../mutations/unblock-user-personally-mutation'
import {canBlock, canBlockPersonally, canUnblock, canUnblockPersonally} from '../utils/comment-actions'
import {type SelectionContext, selectQuoteFromComment} from '../utils/quotes'
import styles from './CommentActions.module.css'
import {BlockUserFromOrgDialog, type BlockUserFromOrgDialogProps} from './issue-comment/BlockUserFromOrgDialog'
import {type HIDE_OPTIONS, HideCommentActionItems} from './issue-comment/HideCommentActions'
import {PersonalBlockUserDialog} from './issue-comment/PersonalBlockUserDialog'
import {ReportContentDialog} from './issue-comment/ReportContentDialog'
import {UnblockUserFromOrgDialog, type UnblockUserFromOrgDialogProps} from './issue-comment/UnblockUserFromOrgDialog'

const CreateIssueDialogEntry = React.lazy(async () => {
  const m = await import('@github-ui/issue-create/CreateIssueDialogEntry')
  return {default: m.CreateIssueDialogEntry}
})

export interface CommentData {
  authorAssociation: GraphQlCommentAuthorAssociation
  body: string
  bodyForClipboard?: string
  id: string
  createdAt: string
  isHidden: boolean
  referenceText: string
  minimizedReason: string | null | undefined
  pendingMinimizeReason?: string | null | undefined
  repository: {
    id: string
    isPrivate: boolean
    name: string
    owner: {
      id: string
      login: string
      url: string
    }
  }
  url: string
  createdViaEmail?: boolean
  viaApp?: {
    name: string
    url: string
  }
  viewerCanDelete: boolean
  viewerCanMinimize: boolean
  viewerCanUnminimize: boolean
  viewerCanPin?: boolean
  viewerCanUnpin?: boolean
  viewerCanSeeMinimizeButton: boolean
  viewerCanSeeUnminimizeButton: boolean
  viewerCanUpdate: boolean
  viewerCanReport: boolean
  viewerCanReportToMaintainer: boolean
  viewerCanBlockFromOrg: boolean
  viewerCanUnblockFromOrg: boolean
  viewerCanBlockPersonally?: boolean
  viewerCanUnblockPersonally?: boolean
  viewerCanReferenceInIssue?: boolean
  viewerCanQuoteReply?: boolean
  author:
    | {
        id: string
        login: string
      }
    | null
    | undefined
  authorToRepoOwnerSponsorship?: {
    createdAt: string
    isActive: boolean
  } | null
  __id?: string
  pinnedBy?: {
    login: string
  } | null
}

export type CommentActionsProps = {
  comment: CommentData
  commentAuthorLogin: string
  editComment: () => void
  onReplySelect: (quotedText?: string) => void
  isMinimized: boolean
  isReply?: boolean
  navigate: (url: string) => void
  navigationSource?: string
  onBlock?: BlockUserFromOrgDialogProps['onBlock']
  onUnblock?: UnblockUserFromOrgDialogProps['onUnblock']
  onMinimize?: (value: boolean) => void
  hideComment: (reason: HIDE_OPTIONS) => void
  unhideComment: () => void
  deleteComment: () => void
  onSuccessfulBlock?: () => void
  commentRef?: React.RefObject<HTMLDivElement | null>
  isDisabled?: boolean
  pinComment?: (commentId: string) => void
  unpinComment?: (commentId: string) => void
  onEditHover?: () => void
}

export function CommentActions({
  comment,
  commentAuthorLogin,
  editComment,
  onReplySelect,
  isMinimized = false,
  isReply = false,
  navigate,
  navigationSource,
  onBlock,
  onUnblock,
  onMinimize: onMinimized,
  hideComment,
  unhideComment,
  deleteComment,
  onSuccessfulBlock,
  commentRef,
  isDisabled = false,
  pinComment,
  unpinComment,
  onEditHover,
}: CommentActionsProps) {
  const [menuOpened, setMenuOpened] = useState(false)
  const [selection, setSelection] = useState<SelectionContext | null>(null)
  const [isNewIssueOpen, setIsNewIssueOpen] = useState(false)
  const [isReportContentOpen, setIsReportContentOpen] = useState(false)
  const [isBlockUserFromOrgOpen, setIsBlockUserFromOrgOpen] = useState(false)
  const [isUnblockUserFromOrgOpen, setIsUnblockUserFromOrgOpen] = useState(false)
  const [isPersonalBlockOpen, setIsPersonalBlockOpen] = useState(false)
  const [isPersonalUnblockOpen, setIsPersonalUnblockOpen] = useState(false)
  const {addToast} = useToastContext()
  const formatBodyAsReference = useCallback(
    (str: string) => {
      return `${str.replace(/^(?!>)/gm, '> ')} \n\n _Originally posted by @${commentAuthorLogin} in [${
        comment.referenceText
      }](${comment.url})_`
    },
    [comment.referenceText, commentAuthorLogin, comment.url],
  )

  const copyToClipboard = (textToCopy: string) => {
    copyText(textToCopy)
  }

  const commentHidden = !!comment.pendingMinimizeReason || comment.isHidden

  const minimizeButton = useMemo(() => {
    return !commentHidden || isMinimized || !comment.viewerCanSeeMinimizeButton ? null : (
      <IconButton
        aria-label={'hide comment'}
        variant={'invisible'}
        onClick={() => onMinimized?.(true)}
        icon={FoldIcon}
        size={'small'}
        className={styles.CommentActionsIconButton}
      />
    )
  }, [commentHidden, isMinimized, onMinimized, comment.viewerCanSeeMinimizeButton])

  const unminimizeButton = useMemo(() => {
    return !commentHidden || !isMinimized || !comment.viewerCanSeeUnminimizeButton ? null : (
      <IconButton
        aria-label={'show comment'}
        variant={'invisible'}
        onClick={() => onMinimized?.(false)}
        icon={UnfoldIcon}
        size={'small'}
        className={styles.CommentActionsIconButton}
      />
    )
  }, [commentHidden, isMinimized, onMinimized, comment.viewerCanSeeUnminimizeButton])

  const reportUrl = useMemo(() => {
    if (!comment.author) return undefined
    const host = ssrSafeWindow?.location.origin
    if (!host) return undefined
    const query = `content_url=${encodeURIComponent(comment.url)}&report=${commentAuthorLogin}+(user)`
    const url = `${host}/contact/report-content?${query}`

    return url
  }, [commentAuthorLogin, comment.author, comment.url])

  const showReportSection = (comment.viewerCanReport || comment.viewerCanReportToMaintainer) && comment.author

  const isAuthorCopilot = isCopilot(commentAuthorLogin)

  const userCanBlock = canBlock({
    viewerCanBlockFromOrg: comment.viewerCanBlockFromOrg,
    hasAuthor: !!comment.author,
    isAuthorCopilot,
  })

  const userCanUnblock = canUnblock({
    viewerCanUnblockFromOrg: comment.viewerCanUnblockFromOrg,
    hasAuthor: !!comment.author,
  })

  const userCanBlockPersonally = canBlockPersonally({
    viewerCanBlockPersonally: comment.viewerCanBlockPersonally,
    hasAuthor: !!comment.author,
    isAuthorCopilot,
  })

  const userCanUnblockPersonally = canUnblockPersonally({
    viewerCanUnblockPersonally: comment.viewerCanUnblockPersonally,
    hasAuthor: !!comment.author,
  })

  // Default value for reference in issue creation is true
  const userCanReferenceInIssue =
    typeof comment.viewerCanReferenceInIssue === 'boolean' ? comment.viewerCanReferenceInIssue : true

  // Default value for quoting in reply is true
  const userCanQuoteReply = typeof comment.viewerCanQuoteReply === 'boolean' ? comment.viewerCanQuoteReply : true

  const isPinned = Boolean(comment.pinnedBy)
  const userCanPin = comment.viewerCanPin && !isPinned && pinComment !== undefined
  const userCanUnpin = comment.viewerCanUnpin && isPinned && unpinComment !== undefined

  const handlePin = () => {
    pinComment?.(comment.id)
  }
  const handleUnpin = () => {
    unpinComment?.(comment.id)
  }

  const issueCreateProps: CreateIssueDialogEntryProps = {
    isCreateDialogOpen: isNewIssueOpen,
    setIsCreateDialogOpen: setIsNewIssueOpen,
    navigate,
    optionConfig: {
      storageKeyPrefix: comment.id,
      defaultDisplayMode: 'IssueCreation' as const,
      scopedOrganization: comment.repository.owner.login,
      issueCreateArguments: {
        initialValues: {
          body: formatBodyAsReference(comment.body),
        },
        repository: {
          name: comment.repository.name,
          owner: comment.repository.owner.login,
        },
      },
    },
  }
  const createdAtFormatted = ariaLabelDate(new Date(comment.createdAt))
  const commentActionsLabel = `Actions for ${commentAuthorLogin}'s ${
    isReply ? 'reply' : 'comment'
  }, ${createdAtFormatted}`

  return (
    <>
      {isNewIssueOpen && (
        <Suspense fallback={null}>
          <CreateIssueDialogEntry {...issueCreateProps} />
        </Suspense>
      )}
      {isReportContentOpen && (
        <ReportContentDialog
          owner={comment.repository.owner.login}
          repository={comment.repository.name}
          ownerUrl={comment.repository.owner.url}
          reportUrl={reportUrl}
          contentId={comment.id}
          onClose={() => setIsReportContentOpen(false)}
          contentType={'comment'}
        />
      )}
      {isPersonalBlockOpen && comment.author && (
        <PersonalBlockUserDialog
          contentId={comment.id}
          contentAuthor={comment.author}
          contentUrl={comment.url}
          navigationSource={navigationSource}
          onClose={() => setIsPersonalBlockOpen(false)}
        />
      )}
      {isPersonalUnblockOpen && comment.author && (
        <ConfirmationDialog
          title={`Unblock @${comment.author.login}`}
          confirmButtonContent="Unblock user"
          confirmButtonType="danger"
          onClose={gesture => {
            const authorLogin = comment.author?.login
            if (gesture === 'confirm' && authorLogin) {
              void (async () => {
                try {
                  await unblockUserPersonally(authorLogin, navigationSource)
                  window.location.reload()
                } catch {
                  // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
                  addToast({type: 'error', message: 'Failed to unblock user'})
                }
              })()
            }
            setIsPersonalUnblockOpen(false)
          }}
        >
          Are you sure you want to unblock @{comment.author.login}?
        </ConfirmationDialog>
      )}
      {isBlockUserFromOrgOpen && comment.author && (
        <BlockUserFromOrgDialog
          onBlock={onBlock}
          onClose={() => {
            onSuccessfulBlock?.()
            setIsBlockUserFromOrgOpen(false)
          }}
          organization={comment.repository.owner}
          contentId={comment.id}
          contentAuthor={comment.author}
          contentUrl={comment.url}
          navigationSource={navigationSource}
        />
      )}
      {isUnblockUserFromOrgOpen && comment.author && (
        <UnblockUserFromOrgDialog
          onClose={() => setIsUnblockUserFromOrgOpen(false)}
          onUnblock={onUnblock}
          organization={comment.repository.owner}
          contentAuthor={comment.author}
        />
      )}
      {minimizeButton}
      {unminimizeButton}
      <ActionMenu
        open={menuOpened}
        onOpenChange={(value: boolean) => {
          if (value) {
            const current = window.getSelection()
            if (current && current.anchorNode) {
              setSelection({
                anchorNode: current.anchorNode,
                range: current.getRangeAt(0),
              })
            } else {
              setSelection(null)
            }
          }
          setMenuOpened(value)
        }}
      >
        <ActionMenu.Anchor data-testid={TEST_IDS.commentHeaderHamburger}>
          <IconButton
            disabled={isDisabled}
            size="small"
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label={commentActionsLabel}
            tooltipDirection="n"
            description="More actions"
            className={styles.CommentActionsIconButton}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList data-testid={TEST_IDS.commentHeaderHamburgerOpen}>
            <ActionList.Item
              inactiveText={comment.url === '' ? 'Comment must have a URL to be copied' : undefined}
              onSelect={() => copyToClipboard(comment.url)}
            >
              <ActionList.LeadingVisual>
                <LinkIcon />
              </ActionList.LeadingVisual>
              Copy link
            </ActionList.Item>
            <ActionList.Item onSelect={() => copyToClipboard(comment.bodyForClipboard || comment.body)}>
              <ActionList.LeadingVisual>
                <MarkdownIcon />
                {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
              </ActionList.LeadingVisual>
              Copy Markdown
            </ActionList.Item>
            {isLoggedIn() && (
              <>
                {userCanQuoteReply && (
                  <ActionList.Item
                    onSelect={() => {
                      const newValue = selectQuoteFromComment(commentRef?.current, selection, comment.body)
                      onReplySelect(newValue)
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <QuoteIcon />
                    </ActionList.LeadingVisual>
                    Quote reply
                  </ActionList.Item>
                )}
                {userCanReferenceInIssue && (
                  <>
                    <ActionList.Divider />
                    <ActionList.Item
                      data-testid={TEST_IDS.commentMenuRefComment}
                      onSelect={() => setIsNewIssueOpen(true)}
                      className={styles.CommentActionsListItem}
                    >
                      <ActionList.LeadingVisual>
                        <IssueOpenedIcon />
                      </ActionList.LeadingVisual>
                      Reference in a new issue
                    </ActionList.Item>
                  </>
                )}
              </>
            )}
            {(comment.viewerCanUpdate ||
              comment.viewerCanMinimize ||
              comment.viewerCanPin ||
              comment.viewerCanUnpin ||
              comment.viewerCanDelete) && <ActionList.Divider />}
            {comment.viewerCanUpdate && (
              <ActionList.Item onSelect={() => editComment()} onMouseEnter={onEditHover}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
            )}
            {userCanPin && (
              <ActionList.Item onSelect={handlePin}>
                <ActionList.LeadingVisual>
                  <PinIcon />
                </ActionList.LeadingVisual>
                Pin
              </ActionList.Item>
            )}
            {userCanUnpin && (
              <ActionList.Item onSelect={handleUnpin}>
                <ActionList.LeadingVisual>
                  <PinSlashIcon />
                </ActionList.LeadingVisual>
                Unpin
              </ActionList.Item>
            )}
            {comment.viewerCanMinimize && !commentHidden && (
              <ActionMenu>
                <ActionMenu.Anchor>
                  <ActionList.Item>
                    <ActionList.LeadingVisual>
                      <EyeClosedIcon />
                    </ActionList.LeadingVisual>
                    Hide
                  </ActionList.Item>
                </ActionMenu.Anchor>

                <ActionMenu.Overlay>
                  <ActionList>
                    <HideCommentActionItems onSelect={hideComment} setMenuOpen={setMenuOpened} />
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            )}
            {comment.viewerCanUnminimize && commentHidden && (
              <ActionList.Item onSelect={unhideComment}>
                <ActionList.LeadingVisual>
                  <EyeIcon />
                </ActionList.LeadingVisual>{' '}
                Unhide
              </ActionList.Item>
            )}
            {comment.viewerCanDelete && (
              <ActionList.Item variant="danger" onSelect={deleteComment}>
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            )}
            {showReportSection && (
              <>
                <ActionList.Divider />
                {comment.viewerCanReportToMaintainer ? (
                  <ActionList.Item
                    onSelect={() => {
                      setIsReportContentOpen(true)
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.Item>
                ) : (
                  <ActionList.LinkItem href={reportUrl} target="_blank">
                    <ActionList.LeadingVisual>
                      <ReportIcon />
                    </ActionList.LeadingVisual>
                    Report content
                  </ActionList.LinkItem>
                )}
              </>
            )}
            {/* TODO view in stafftools */}
            {(userCanBlockPersonally || userCanUnblockPersonally || userCanBlock || userCanUnblock) &&
              !showReportSection && <ActionList.Divider />}
            {userCanBlockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalBlockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {userCanUnblockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalUnblockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
            {!userCanBlockPersonally && !userCanUnblockPersonally && userCanBlock && (
              <ActionList.Item onSelect={() => setIsBlockUserFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {!userCanBlockPersonally && !userCanUnblockPersonally && userCanUnblock && (
              <ActionList.Item onSelect={() => setIsUnblockUserFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}
